import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { TextField, Button, Snackbar, Alert, CircularProgress, Box, Typography } from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL;

const OTPValidationRequest = ({ postId, userId }) => {
  const [otp, setOtp] = useState('');
  const [validationStatus, setValidationStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Check initial validation status when component loads
    checkValidationStatus();
  }, []); // Empty dependency array to run only once

  const checkValidationStatus = async () => {
    try {
      // Send the post_id and user_id in the request body
      const response = await axios.post(`${API_URL}/api/booking-status-request/`, {
        post_id: postId,
        user_id: userId,
      });

      // If the response contains validation status
      if (response.data.validation === 'success') {
        setValidationStatus('success');
        setMessage('OTP has already been validated!');
        setOpen(true);
      }
    } catch (error) {
      console.log('Error fetching validation status', error);
      // If the API response has an error
      if (error.response) {
        setMessage(error.response.data.error || 'Something went wrong');
        setOpen(true);
      }
    }
  };

  const handleOtpSubmit = async () => {
    if (otp.trim() === '') return; // Prevent empty OTP submission

    setIsLoading(true);
    try {
      const response = await axios.post(`${API_URL}/validate-otp-request/`, {
        session_id: postId,
        user_id: userId,
        otp: otp,
      });

      if (response.status === 200) {
        setValidationStatus('success');
        setMessage(response.data.message);
        setOpen(true);
      }
    } catch (error) {
      setValidationStatus('failed');
      setMessage(error.response?.data?.message || 'Something went wrong');
      setOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#2c2c2c',
        borderRadius: '8px',
        padding: '2rem',
        boxShadow: 3,
        mt:5,
        width: { xs: '90%', sm: '80%', md: '60%' }, // Responsive width for different screen sizes
      }}
    >
      {/* Title or Helper Text */}
      <Typography variant="h5" component="h1" sx={{ color: '#fff', marginBottom: '1rem' , textAlign:'center', letterSpacing: 4}}>
        OTP Validation
      </Typography>

      {validationStatus === 'success' ? (
        <div>
          <Typography variant="body1" sx={{ color: '#66bb6a', fontWeight: '600' , textAlign:'center', letterSpacing: 3}}>
            OTP has been successfully validated!
          </Typography>
        </div>
      ) : (
        <div>
          <TextField
            label="Enter OTP"
            variant="outlined"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            fullWidth
            sx={{
              marginBottom: '1rem',
              letterSpacing: 3,
              '& .MuiInputBase-root': {
                backgroundColor: '#3a3a3a', // Dark background for input field
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#666', // Lighter border color for dark theme
              },
              '& .MuiInputLabel-root': {
                color: '#e0e0e0', // Lighter label color
              },
            }}
          />
          <Typography variant="body2" sx={{ color: '#e0e0e0', marginBottom: '1rem', letterSpacing: 3 }}>
            Enter the OTP sent to your email or phone to validate this booking.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOtpSubmit}
            disabled={isLoading || otp.trim() === ''}
            sx={{
              padding: '1rem 2rem',
              borderRadius: '50px',
              fontWeight: 'bold',
              letterSpacing: 3,
              background: 'linear-gradient(45deg, #1e90ff, #1c70ff)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1e90ff, #1c70ff)',
                transform: 'scale(1.05)',
                boxShadow: '0 18px 30px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            {isLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Validate OTP'}
          </Button>
        </div>
      )}

      {/* Snackbar for error or success */}
      <Snackbar open={open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={validationStatus === 'success' ? 'success' : 'error'} sx={{letterSpacing: 4}}>
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OTPValidationRequest;
